import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send, Sparkles, User, Loader2, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const AIChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'bot', text: "Hi there! I'm Epic AI, the EpicEdge Creative assistant. How can I help you today?" }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Generate or retrieve a persistent session ID for this browser session
    const sessionId = useRef((() => {
        let id = sessionStorage.getItem('epic_ai_session');
        if (!id) {
            id = 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
            sessionStorage.setItem('epic_ai_session', id);
        }
        return id;
    })());

    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userText = input.trim();
        setInput('');

        const newMessages = [...messages, { role: 'user', text: userText }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            const response = await axios.post('/api/ai/chat', {
                message: userText,
                history: messages,
                sessionId: sessionId.current
            });

            if (response.data && response.data.reply) {
                setMessages([...newMessages, { role: 'bot', text: response.data.reply }]);
            } else {
                throw new Error('Invalid response');
            }
        } catch (error) {
            console.error('AI Chat Error:', error);
            setMessages([...newMessages, { role: 'bot', text: 'Sorry, I am having trouble connecting right now. Please try again later or contact our team directly!' }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Floating Action Button - Clean Branded Nanopill */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-6 right-6 z-50 flex items-center gap-1.5 px-2 py-1.5 rounded-full shadow-lg transition-all duration-500 transform hover:scale-110 active:scale-90 ${isOpen ? 'translate-y-20 opacity-0 pointer-events-none' : 'bg-gradient-to-r from-amber-400 to-amber-500 border border-amber-300'
                    }`}
                aria-label="Open AI Assistant"
            >
                <div className="flex items-center justify-center pl-0.5 shrink-0">
                    <Bot className="w-5 h-5 text-slate-900" />
                </div>
                <div className="flex flex-col items-start leading-none pr-1">
                    <span className="text-[10px] font-black text-slate-900 uppercase tracking-tighter">Epic AI</span>
                    <div className="flex items-center gap-1 mt-0.5">
                        <div className="w-1 h-1 bg-green-600 rounded-full animate-pulse"></div>
                        <span className="text-[8px] font-black text-slate-800 uppercase tracking-tight">Online</span>
                    </div>
                </div>
            </button>

            {/* Chat Window */}
            <div
                className={`fixed bottom-6 right-6 z-50 w-[260px] sm:w-[300px] h-[420px] max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 transform origin-bottom-right ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-75 opacity-0 translate-y-10 pointer-events-none'
                    }`}
            >
                {/* Header - Ultra Compact */}
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-2.5 flex items-center justify-between shrink-0 relative overflow-hidden">
                    {/* Decorative glass overlay */}
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>

                    <div className="flex items-center gap-2 relative z-10 mr-auto">
                        <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg border-2 border-slate-700">
                            <Sparkles className="w-4 h-4 text-slate-900" />
                        </div>
                        <div>
                            <h3 className="font-bold text-white text-[13px] leading-none mb-0.5">Epic AI</h3>
                            <div className="flex items-center gap-1">
                                <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Live</span>
                            </div>
                        </div>
                    </div>

                    {/* WhatsApp Action Button - Ultra Compact */}
                    <a
                        href="https://wa.me/254787205456"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative z-10 flex items-center gap-1 px-2 py-0.5 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all hover:scale-105 active:scale-95 shadow-lg shadow-green-500/20 mr-2"
                    >
                        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        <span className="text-[6px] font-black uppercase tracking-tight">WhatsApp</span>
                    </a>

                    <button
                        onClick={() => setIsOpen(false)}
                        className="relative z-10 p-1 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-300 hover:text-white transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5 bg-slate-50/50 scroll-smooth no-scrollbar">
                    {messages.map((msg, idx) => (
                        <div
                            key={idx}
                            className={`flex items-end gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                        >
                            {/* Avatar */}
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-slate-200 text-slate-500' : 'bg-gradient-to-br from-amber-400 to-yellow-600'
                                }`}>
                                {msg.role === 'user' ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3 text-slate-900" />}
                            </div>

                            {/* Bubble */}
                            <div
                                className={`max-w-[85%] px-2.5 py-1.5 rounded-xl shadow-sm text-[12px] leading-snug font-medium ${msg.role === 'user'
                                    ? 'bg-slate-900 text-white rounded-br-none'
                                    : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none prose prose-sm prose-amber'
                                    }`}
                            >
                                <ReactMarkdown
                                    components={{
                                        p: ({ node, ...props }) => <p className="mb-1 last:mb-0" {...props} />,
                                        a: ({ node, ...props }) => <a className="text-amber-600 underline font-black" target="_blank" rel="noopener noreferrer" {...props} />,
                                        strong: ({ node, ...props }) => <strong className="font-black text-gray-900" {...props} />,
                                        ul: ({ node, ...props }) => <ul className="list-disc pl-3 mb-1" {...props} />,
                                        ol: ({ node, ...props }) => <ol className="list-decimal pl-3 mb-1" {...props} />,
                                        li: ({ node, ...props }) => <li className="mb-0.5" {...props} />
                                    }}
                                >
                                    {msg.text}
                                </ReactMarkdown>
                            </div>
                        </div>
                    ))}

                    {/* Typing Indicator */}
                    {isLoading && (
                        <div className="flex items-end gap-2">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shrink-0 shadow-sm">
                                <Sparkles className="w-3 h-3 text-slate-900" />
                            </div>
                            <div className="bg-white border border-gray-100 px-2 py-1.5 rounded-xl rounded-bl-none shadow-sm flex items-center gap-1">
                                <span className="w-1 h-1 bg-amber-400 rounded-full animate-bounce"></span>
                                <span className="w-1 h-1 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                                <span className="w-1 h-1 bg-amber-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-2.5 bg-white border-t border-gray-100 shrink-0">
                    <form onSubmit={handleSubmit} className="relative flex items-center">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type..."
                            disabled={isLoading}
                            className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-[12px] rounded-xl pl-3 pr-9 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-shadow disabled:opacity-50"
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                            className="absolute right-1 p-1.5 bg-slate-900 text-white rounded-lg disabled:opacity-30 hover:bg-amber-600 transition-all"
                        >
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        </button>
                    </form>
                    <div className="text-center mt-1">
                        <span className="text-[7px] text-slate-400 font-bold uppercase tracking-[0.15em]">EpicRAG Smart Module</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AIChatWidget;
